import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icon, Sheet, Text, useSheetDismiss } from '../../../../components';
import { useSortableRows } from '../../../blog-editor/hooks/useSortableRows';
import { SCREEN_CHROME } from '../../game-list-geometry';
import {
  reorderChange,
  reorderedRows,
  UNTOUCHED_COPY,
  withRowHidden,
  withRowMoved,
  type ReorderChange,
  type ReorderCopy,
  type ReorderRow,
} from '../../list-reorder';
import type { OwnListMode } from '../../owner-game-lists-api';
import type { ListMove } from '../../page-lists';
import * as S from './ListReorderModal.styles';

/** One list on the page, labelled by the caller, so this stays pure. */
export type ReorderListItem = {
  /** The page row's key (`PageListRow.key`): unique on the page, what Save hands back. */
  id: string;
  label: string;
  count?: number;
  hidden: boolean;
  /** One of the owner's own lists: the own-list disc, "Custom" in its meta line, and Edit. */
  own: boolean;
  /** How an own list is built; absent on a site list. */
  mode?: OwnListMode;
};

export type ListReorderModalProps = {
  /** The page's name, for the subtitle ("Homepage"). */
  pageLabel: string;
  /** The page's lists, in page order. Empty while the page's arrangement is not available. */
  items: ReorderListItem[];
  /** Edit the own list with this row key, in the wizard over this sheet (which stays open beneath with its copy). */
  onEdit?: (id: string) => void;
  /** Save order: the rows as shown (for their flags) and the owner's moves in the order made, to apply to the page as
   *  ONE change, each move as the drag it was (`withListRows`). Returns true when the page took it (the sheet then
   *  closes) and false when it was held (the sheet stays open with its copy). */
  onSave: (rows: readonly ReorderRow[], moves: readonly ListMove[]) => boolean;
  /** A publish runs: Save order is held, as the page's move and hide handlers were. */
  busy?: boolean;
  /** One of the owner's lists is being stored (a create, a rename): Edit waits for it, as Configure list does, so the
   *  editor opens on what was stored, not on what the write is about to replace. */
  listWriting?: boolean;
  onClose: () => void;
};

/**
 * Reorder lists: the page's game lists, top to bottom, to put in order and to hide or show. The order uses the
 * same drag as the blog's reading-order list (press-and-hold or the handle to lift, Arrow Up / Down from the
 * keyboard), plus Move up / Move down and a Hide per list. All of it changes a working copy only: Save order
 * hands the copy to the page as one change, every move made as the drag it was, and every other way out (Cancel,
 * the close button, Escape, the backdrop) drops it with the sheet. A hidden list keeps its place, so showing it
 * again puts it back there.
 */
export const ListReorderModal = ({
  pageLabel,
  items,
  onEdit,
  onSave,
  busy = false,
  listWriting = false,
  onClose,
}: ListReorderModalProps): React.ReactElement => {
  const { t } = useTranslation();
  const { closeHandle, dismiss } = useSheetDismiss(onClose);
  // The owner's moves and hides. The rows shown are the page's lists as they are now with those moves made on them,
  // as Save order will make them: one deleted meanwhile (from Edit, over this sheet) drops out with its moves, and one
  // the page gained shows where the page has it, since none of the owner's moves moves it. Derived on every render.
  const [copy, setCopy] = React.useState<ReorderCopy>(UNTOUCHED_COPY);
  // Set once the page takes Save order (see `save`): the sheet is leaving, and until it is gone Save order refuses, as
  // does every control that changes the copy.
  const spent = React.useRef(false);
  const rows = reorderedRows(copy, items);
  const byId = new Map(items.map((item) => [item.id, item]));
  const keys = rows.map((row) => row.key);

  const sortable = useSortableRows({
    keys,
    onMove: (from, to) => {
      if (spent.current) return;
      setCopy((prev) => withRowMoved(prev, items, from, to));
    },
    longPress: true,
    autoScroll: SCREEN_CHROME.desktop,
  });
  // A row in the hand, or let go and still easing into its place: its move reaches the copy only once it lands
  // (SORTABLE_ANIM.SETTLE_MS after the release). Until then Save order and the arrows (Move up / down, and the handle's
  // Arrow Up / Down, as a mouse drag leaves the focus on the handle) do nothing: Save order would save the copy without
  // the drag, and an arrow would move the rows the drag's from/to then count on.
  const settling = keys.some((_, index) => sortable.row(index).dragged);

  // Against the page's lists as they are now: a list the page gained meanwhile counts as it came, so hiding it reads
  // as a change, and a move undone still reads as one (see `reorderChange`).
  const change = reorderChange(copy, items);
  // The change Save order handed the page, once it took it: the status keeps saying it while the sheet leaves.
  const [spentChange, setSpentChange] = React.useState<ReorderChange>('none');
  const shownChange = change !== 'none' ? change : spentChange;

  // The Save order presses a publish has held: each one keys the status's wait sentence anew (see the status).
  const [heldPresses, setHeldPresses] = React.useState(0);

  const save = (): void => {
    if (spent.current) return;
    if (busy) {
      setHeldPresses((n) => n + 1);
      return;
    }
    if (settling) return;
    if (change === 'none') {
      dismiss();
      return;
    }
    if (!onSave(rows, copy.moves)) return;
    // Taken, the copy is spent: the page has it, and gives its lists back in the order saved. Kept while the sheet
    // leaves (still live, the focus on Save order), it was made on that order again: the rows shown out of the order
    // saved. Emptied, the rows are the page as saved. Spent, Save order refuses every press from now on, as the copy's
    // controls do: a Hide, an arrow or a drag over the exit made a change again, and another press saved it from a
    // sheet already leaving. The status keeps the change's words: emptied with the copy, it gave up its line on a phone
    // as the sheet began to leave. A refused save (busy, settling, or held by the page) spends nothing.
    spent.current = true;
    setSpentChange(change);
    setCopy(UNTOUCHED_COPY);
    dismiss();
  };

  const n = rows.length;
  const m = rows.filter((row) => row.hidden).length;
  const listsPart = n === 1 ? t('1 list') : t('{{n}} lists', { n });
  const hiddenPart = m === 0 ? '' : m === 1 ? t('1 hidden') : t('{{m}} hidden', { m });
  const subtitle = `${[pageLabel, listsPart, hiddenPart].filter(Boolean).join(' · ')}. ${t(
    'Drag a row or use the arrows. Your site updates once you publish.',
  )}`;

  return (
    <Sheet
      title="Reorder lists"
      maxWidth="960px"
      onClose={onClose}
      closeHandle={closeHandle}
      footer={
        <S.FooterRow>
          {/* Always there, and only its words change: a live region that arrives with its words is often not
              announced. While a publish runs, Save order only looks faded (the shared Button takes no
              `aria-disabled`), so this says why it waits. The sheet is busy only when it was opened while a publish
              ran (a publish starts from the tab, under the backdrop), so the region arrives with these words: each
              press it holds puts them in again, as a new node under a new key, which the region announces. Literal
              text per branch, so the extractor harvests each. */}
          <S.Status role="status">
            {busy ? (
              <Text
                key={heldPresses}
                tag="span"
                text="Publishing your changes. Save order is available when it finishes."
              />
            ) : shownChange !== 'none' ? (
              <>
                <S.StatusDot aria-hidden="true" />
                {shownChange === 'order' ? (
                  <Text tag="span" text="Order changed. Not published yet." />
                ) : (
                  <Text tag="span" text="Changes not published yet." />
                )}
              </>
            ) : null}
          </S.Status>
          <S.FooterActions>
            <Button variant="text" label={t('Cancel')} onClick={dismiss} />
            <S.PrimaryBtn variant="primary" label={t('Save order')} $off={busy} onClick={save} />
          </S.FooterActions>
        </S.FooterRow>
      }
    >
      <S.Subtitle>{subtitle}</S.Subtitle>
      <S.List ref={sortable.listRef}>
        {rows.map((row, index) => {
          const item = byId.get(row.key);
          if (!item) return null;
          const state = sortable.row(index);
          const handle = sortable.handleProps(index);
          const name = item.label;
          const countPart =
            item.count === 1 ? t('1 game') : item.count != null ? t('{{count}} games', { count: item.count }) : '';
          // "Custom" in the list's own context: it agrees with "list" where the language has a gender, next to the
          // feminine "Automatic" (es and pt "Personalizada · Automática"). The shared "Custom" has other users.
          const kind = item.own
            ? [t('Custom', { context: 'list' }), item.mode === 'auto' ? t('Automatic') : t('Hand-picked')]
            : [t('Default list')];
          const meta = [countPart, ...kind].filter(Boolean).join(' · ');
          const first = index === 0;
          const last = index === rows.length - 1;
          return (
            <S.Row
              key={row.key}
              data-list-key={row.key}
              $shift={state.shift}
              $dragged={state.dragged}
              $animated={state.animated}
              $hidden={row.hidden}
              {...sortable.rowProps(index)}
            >
              <S.Handle
                type="button"
                aria-label={t('Move {{name}}', { name })}
                {...handle}
                onKeyDown={(event) => {
                  if ((settling || spent.current) && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
                    event.preventDefault();
                    return;
                  }
                  handle.onKeyDown(event);
                }}
              >
                <Icon name="drag-handle" size={18} />
              </S.Handle>
              <S.Position $muted={row.hidden}>{state.position + 1}</S.Position>
              {item.own ? (
                <S.OwnMark $hidden={row.hidden} aria-hidden="true" data-testid="own-mark">
                  <Icon name="edit" size={10} />
                </S.OwnMark>
              ) : null}
              <S.Titles>
                {/* An own list's name is data, shown as typed, as on its chip. A site list's goes through the
                    translation, as it always has. */}
                <S.RowTitle text={name} $muted={row.hidden} raw={item.own} />
                <S.RowMeta text={meta} raw />
              </S.Titles>
              {row.hidden ? (
                <S.ScreenReaderText>
                  <Text tag="span" text="Hidden on your site" />
                </S.ScreenReaderText>
              ) : null}
              {item.own && onEdit ? (
                <S.RowBtn
                  type="button"
                  disabled={listWriting}
                  aria-label={t('Edit {{name}}', { name })}
                  onClick={() => onEdit(item.id)}
                >
                  <Icon name="edit" size={14} />
                  <S.RowBtnLabel text="Edit" />
                </S.RowBtn>
              ) : null}
              <S.RowBtn
                type="button"
                $show={row.hidden}
                aria-label={
                  row.hidden ? t('Show {{name}} on your site', { name }) : t('Hide {{name}} from your site', { name })
                }
                onClick={() => {
                  if (spent.current) return;
                  setCopy((prev) => withRowHidden(prev, row.key, !row.hidden));
                }}
              >
                {/* The eye is the list's state, as the column's rows show it (the user, 2026-10-21). */}
                <Icon name={row.hidden ? 'eye-off' : 'eye'} size={14} />
                {row.hidden ? <S.RowBtnLabel text="Show" /> : <S.RowBtnLabel text="Hide" />}
              </S.RowBtn>
              {/* Unavailable at their end by `aria-disabled`, not `disabled`: a button that turns disabled under the
                  keyboard drops the focus to the page, so moving a row to the end lost the owner's place. The
                  handlers refuse instead. */}
              <S.ArrowBtn
                type="button"
                aria-label={t('Move {{name}} up', { name })}
                aria-disabled={first}
                onClick={() => {
                  if (first || settling || spent.current) return;
                  setCopy((prev) => withRowMoved(prev, items, index, index - 1));
                }}
              >
                <Icon name="chevron-up" size={14} />
              </S.ArrowBtn>
              <S.ArrowBtn
                type="button"
                aria-label={t('Move {{name}} down', { name })}
                aria-disabled={last}
                onClick={() => {
                  if (last || settling || spent.current) return;
                  setCopy((prev) => withRowMoved(prev, items, index, index + 1));
                }}
              >
                <Icon name="chevron-down" size={14} />
              </S.ArrowBtn>
            </S.Row>
          );
        })}
      </S.List>
    </Sheet>
  );
};

export default ListReorderModal;
